import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { CocoChatModel } from '../models/coco-chat.model';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

type Params = {
  transcribedText: string;
  chatRef: React.RefObject<{
    appendInputValue: (value: string) => void;
  }>;
};

/**
 * Voice transcription for Coco chat. Mirrors VoiceTranscriptionCompleteAction
 * but tracks with CocoChatModel context for analytics.
 */
export class CocoVoiceTranscriptionCompleteAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Coco Voice Transcription Complete';
  description = 'Handle completed voice transcription for coco chat';
  inputSchema = z.object({
    transcribedText: z.string(),
    chatRef: z.custom<
      React.RefObject<{
        appendInputValue: (value: string) => void;
      }>
    >(),
  });
  discrete = true;

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    if (params.chatRef.current !== null) {
      params.chatRef.current.appendInputValue(params.transcribedText);
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    const hasMessages = model.hasMessages.snapshot();

    return cocoActionMetadata(CocoEvent.VoiceTranscriptionComplete, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
      textLength: payload.transcribedText.length,
      hasContent: payload.transcribedText.trim().length > 0,
      hasMessages,
    });
  }
}
